import React from 'react';
import { AbTest } from '../../../models/AbTest';
import StatusText from '@app/react-common/components/status-text/StatusText';

import ThreeDotMenu from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import { IAbTestActions } from '../../../models/AbTestActions';
import {
  generateAnalyticsDashboardUrl,
  getAbTestDuplicationTestIdString,
  getAbTestType,
  getSetWinnerDialogText,
} from '../../../utils/AbTestingUtils';
import { AbTestOptimizationTarget } from '@pdqdev/pdq-shared/dist/entities';
import { AbTestType } from '../../../models/AbTestType';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import Button from '@app/react-common/components/button/Button';
import { Checkout } from '../../../../../checkout/react/models/Checkout';
import { useAppContext } from '@app/react-common/AppContext';
import { AbTestModal } from '../../ab-test-modal/AbTestModal';
import { AbTestModalV2 } from '../../ab-test-modal/AbTestModalV2';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import Tag from '@app/react-common/components/tag/Tag';
import { AbTestTypeCategory, AbTestWidgetTypeLabels } from '../../../models/AbTestCreationType';
import {
  EXTENSIONS_AB_TEST_ID_PREFIX,
  ExtensionsGroupType,
  buildExtensionsGroupUsageToken,
  ExtensionsTypeToDisplayName,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { AbTestWidgetType } from '../../../models/AbTestCreationType';
import { ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { AbTestGroupMode } from '@pdqdev/pdq-shared/dist/entities/AbTestGroupMode';
import useShop from '@app/react-common/hooks/useShop';
import { IExtensionGroup } from '../../../../../extension-groups/models/extension-group.models';
import { ShopifyTheme } from '../../ab-test-modal/hooks/useThemes';
import { SmartCart } from '../../ab-test-modal/hooks/useSmartCarts';
import { getIsThemeTest, getIsThemeWidget, getWinnerButtonText, shouldUseAbTestModalV2 } from '../../../utils';
import { ThemeWinnerSuccessContent } from '../../AbTestModals/ThemeWinnerSuccessContent';
import { PdpWinnerSuccessContent } from '../../AbTestModals/PdpWinnerSuccessContent';

interface AbTestListItemProps {
  abTest: AbTest;
  abTestActions: IAbTestActions;
  checkouts: Checkout[];
  extensions: ICheckoutExtensions[];
  extensionGroups: IExtensionGroup[];
  runningAbTestCheckoutIds: number[];
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  themes: ShopifyTheme[];
  smartCarts?: SmartCart[];
}

export const AbTestListItem: React.FC<AbTestListItemProps> = ({
  abTest,
  abTestActions,
  checkouts,
  extensions,
  extensionGroups,
  runningAbTestCheckoutIds,
  setActiveTab,
  themes,
  smartCarts,
}) => {
  const [startAbTestOpen, setStartAbTestOpen] = React.useState<boolean>(false);
  const [editAbTestOpen, setEditAbTestOpen] = React.useState<boolean>(false);
  const [deleteAbTestOpen, setDeleteAbTestOpen] = React.useState<boolean>(false);
  const [duplicateAbTestOpen, setDuplicateAbTestOpen] = React.useState<boolean>(false);
  const [endAbTestOpen, setEndAbTestOpen] = React.useState<boolean>(false);
  const [archiveAbTestOpen, setArchiveAbTestOpen] = React.useState<boolean>(false);
  const [viewAbTestOpen, setViewAbTestOpen] = React.useState<boolean>(false);
  const shop = useShop();
  const useAbTestModalV2 = shouldUseAbTestModalV2(shop?.metadata, abTest.testType, abTest.mode);

  const modalActions = {
    setStartAbTestOpen,
    setDeleteAbTestOpen,
    setDuplicateAbTestOpen,
    setEndAbTestOpen,
    setEditAbTestOpen,
    setViewAbTestOpen,
    setArchiveAbTestOpen,
  };
  const [setAsWinnerOpen, setSetAsWinnerOpen] = React.useState<{ show: boolean; id: number }>({
    show: false,
    id: undefined,
  });
  const [forceSetAsWinnerOpen, setForceSetAsWinnerOpen] = React.useState<{ show: boolean; id: number; text: string }>({
    show: false,
    id: undefined,
    text: undefined,
  });
  const { angularRedirect, showToast } = useAppContext();

  if (!abTest) {
    return null;
  }

  const abTestStatusText = getAbTestStatusText(abTest);
  const abTestCategoryTypeLabel = getAbTestCategoryTypeLabel(abTest);

  // Determine widget type from test type or from first widget
  const abTestWidgetType = (() => {
    if (abTest.testType === AbTestTypeCategory.CROSS_SITE) return AbTestWidgetType.CROSS_SITE;
    if (abTest.testType === AbTestTypeCategory.PRODUCT_PAGE) return AbTestWidgetType.PRODUCT_PAGE;

    const firstWidget = abTest.abTestVariants?.[0]?.abTestWidgets?.[0];
    if (firstWidget) return firstWidget.type as AbTestWidgetType;

    // Fallback to old logic (including new EXTENSION_GROUPS support)
    return abTest.abTestVariants.find((variant) => variant.abTestWidgets.find((widget) => widget.type === 'checkout'))
      ? AbTestWidgetType.CHECKOUT
      : abTest.abTestVariants.find((variant) =>
            variant.abTestWidgets.find((widget) => widget.type === AbTestWidgetType.EXTENSION_GROUPS),
          )
        ? AbTestWidgetType.EXTENSION_GROUPS
        : AbTestWidgetType.EXTENSION;
  })();

  const isThemeTest = getIsThemeTest(abTest.testType);
  const isPdpTest = abTest.testType === AbTestTypeCategory.PRODUCT_PAGE;
  const isCrossSiteTest = abTest.testType === AbTestTypeCategory.CROSS_SITE;

  return (
    <div key={abTest.name} className="ab-test-item">
      <div className="ab-test-item__header">
        <h3 className="ab-test-item__header__title">{abTest.name}</h3>
        <Tag
          value={abTestCategoryTypeLabel}
          size="large"
          padding={true}
          color="var(--on-surface-default)"
          textColor="var(--surface-bright)"
        />
        <div className="ab-test-item__header__optimize">
          {false && (
            <>
              <p className="ab-test-item__header__optimize__title">Optimize for</p>
              <p className="ab-test-item__header__optimize__value">{AbTestOptimizationTarget[abTest.optimizingFor]}</p>
            </>
          )}
        </div>
        <div className="ab-test-item__header__daterange">
          {getAbTestType(abTest) != AbTestType.DRAFT && (
            <span>
              {getDateRangeDisplayDate(abTest.startedAt)} - {getDateRangeDisplayDate(abTest.endedAt)}
            </span>
          )}
        </div>
        <div className="ab-test-item__header__status">
          <StatusText label={abTestStatusText} color={getColorByStatus(abTestStatusText)} />
        </div>
        <div className="ab-test-item__header__three-dot">
          <ThreeDotMenu items={getAbTestThreeDotMenuItems(abTest, modalActions, angularRedirect, showToast)} />
        </div>
      </div>
      {abTest.abTestVariants && abTest.abTestVariants.length > 0 && (
        <>
          <div className="ab-test-item__splitter">
            <div className="ab-test-item__splitter__title">
              <p>Variation</p>
            </div>
            <div className="ab-test-item__splitter__traffic">
              <p>Traffic split</p>
            </div>
            {(abTestWidgetType === AbTestWidgetType.EXTENSION ||
              abTestWidgetType === AbTestWidgetType.EXTENSION_GROUPS) && (
              <div className="ab-test-item__splitter__shopify-token">
                <p>Shopify Token: {EXTENSIONS_AB_TEST_ID_PREFIX + abTest.id}</p>
              </div>
            )}
          </div>
          <div className="ab-test-item__variants">
            {abTest.abTestVariants
              .sort(function (a, b) {
                return a.name.localeCompare(b.name);
              })
              .map((variant) => {
                return (
                  <div key={variant.name} className="ab-test-item__variants__variant">
                    <div className="ab-test-item__variants__variant__info">
                      <div className="ab-test-item__variants__variant__letter">{variant.name}</div>
                      {variant.abTestWidgets && variant.abTestWidgets.length > 0 && (
                        <div>
                          {abTestWidgetType === AbTestWidgetType.CHECKOUT && (
                            <>
                              <p className="ab-test-item__variants__variant__name">
                                {useAbTestModalV2 &&
                                abTest.mode === AbTestGroupMode.MULTIPLE_CONTROL_GROUPS &&
                                variant.name === 'A'
                                  ? 'Control Group'
                                  : checkouts.find(
                                      (x) =>
                                        x.id ==
                                        variant.abTestWidgets?.find((y) => y.type == AbTestWidgetType.CHECKOUT)
                                          ?.externalId,
                                    )?.name || 'Deleted checkout'}
                              </p>
                              <p className="ab-test-item__variants__variant__description">
                                {useAbTestModalV2 &&
                                abTest.mode === AbTestGroupMode.MULTIPLE_CONTROL_GROUPS &&
                                variant.name === 'A'
                                  ? ''
                                  : checkouts.find(
                                      (x) =>
                                        x.id ==
                                        variant.abTestWidgets?.find((y) => y.type == AbTestWidgetType.CHECKOUT)
                                          ?.externalId,
                                    )?.description}
                              </p>
                            </>
                          )}
                          {variant.abTestWidgets
                            .filter((widget) => widget.type === AbTestWidgetType.EXTENSION)
                            .map((extensionWidget, index) => {
                              const extension = extensions.find((ext) => ext.id === extensionWidget.externalId);
                              const isNoneExtension = extensionWidget.externalId === null;
                              return (
                                <div key={`extension-${index}`}>
                                  <p className="ab-test-item__variants__variant__name">
                                    {isNoneExtension
                                      ? 'NONE'
                                      : extension?.template?.internalName || 'Deleted extension'}
                                  </p>
                                  {extension?.type && (
                                    <p className="ab-test-item__variants__variant__description">
                                      {ExtensionsTypeToDisplayName[extension.type]}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          {variant.abTestWidgets
                            .filter((widget) => widget.type === AbTestWidgetType.EXTENSION_GROUPS)
                            .map((extensionGroupWidget, index) => {
                              const extensionGroup = extensionGroups.find(
                                (group) => group.id === extensionGroupWidget.externalId,
                              );
                              const isNoneExtensionGroup = extensionGroupWidget.externalId === null;
                              return (
                                <div key={`extension-group-${index}`}>
                                  <p className="ab-test-item__variants__variant__name">
                                    {isNoneExtensionGroup ? 'NONE' : extensionGroup?.name || 'Deleted extension group'}
                                  </p>
                                  {extensionGroup?.description && (
                                    <p className="ab-test-item__variants__variant__description">
                                      {extensionGroup.description}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          {variant.abTestWidgets
                            .filter(
                              (widget) => getIsThemeWidget(widget.type) && widget.type !== AbTestWidgetType.SMART_CART,
                            )
                            .map((themeWidget, index) => {
                              const theme = themes.find((t) => Number(t.id) === Number(themeWidget.externalId));
                              return (
                                <div key={`theme-${index}`}>
                                  <p className="ab-test-item__variants__variant__name">
                                    {theme?.name || 'Deleted theme'}
                                  </p>
                                </div>
                              );
                            })}
                          {variant.abTestWidgets
                            .filter((widget) => widget.type === AbTestWidgetType.SMART_CART)
                            .map((smartCartWidget, index) => {
                              const smartCart = smartCarts?.find(
                                (s) => Number(s.id) === Number(smartCartWidget.externalId),
                              );
                              return (
                                <div key={`smart-cart-${index}`}>
                                  <p className="ab-test-item__variants__variant__name">
                                    {smartCart?.name || 'Deleted Smart Cart'}
                                  </p>
                                  {smartCart?.description && (
                                    <p className="ab-test-item__variants__variant__description">
                                      {smartCart.description}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                    <div className="ab-test-item__variants__variant__percentage">
                      <p>{variant.percentageTraffic}%</p>
                    </div>
                    {abTestWidgetType === AbTestWidgetType.EXTENSION && (
                      <div className="ab-test-item__variants__variant__shopify-token">
                        {variant.abTestWidgets
                          ?.filter((widget) => widget.type === AbTestWidgetType.EXTENSION)
                          .map((extensionWidget, index) => {
                            const extension = extensions.find((ext) => ext.id === extensionWidget.externalId);
                            return <p key={`token-${index}`}>{extension?.usageToken || '-'}</p>;
                          })}
                        {!variant.abTestWidgets?.some((widget) => widget.type === 'extension') && <p>-</p>}
                      </div>
                    )}
                    {abTestWidgetType === AbTestWidgetType.EXTENSION_GROUPS && (
                      <div className="ab-test-item__variants__variant__shopify-token">
                        {variant.abTestWidgets
                          ?.filter((widget) => widget.type === AbTestWidgetType.EXTENSION_GROUPS)
                          .map((extensionGroupWidget, index) => {
                            const extensionGroup = extensionGroups.find(
                              (group) => group.id === extensionGroupWidget.externalId,
                            );
                            return (
                              <p key={`token-${index}`}>
                                {extensionGroup?.id
                                  ? buildExtensionsGroupUsageToken(
                                      extensionGroup.type ?? ExtensionsGroupType.EXTENSIONS,
                                      extensionGroup.id,
                                    )
                                  : '-'}
                              </p>
                            );
                          })}
                        {!variant.abTestWidgets?.some(
                          (widget) => widget.type === AbTestWidgetType.EXTENSION_GROUPS,
                        ) && <p>-</p>}
                      </div>
                    )}
                    <div className="ab-test-item__variants__variant__label-button-container">
                      {variant.winnerAt && (
                        <StatusText
                          className="ab-test-item__variants__variant__winner-label"
                          label={'Winner'}
                          color={'hyperlight20'}
                        />
                      )}
                      {getAbTestType(abTest) === AbTestType.CONCLUDED &&
                        (abTestWidgetType === AbTestWidgetType.CHECKOUT ||
                          abTestWidgetType === AbTestWidgetType.CROSS_SITE ||
                          abTestWidgetType === AbTestWidgetType.PRODUCT_PAGE) && (
                          <Button
                            className="ab-test-item__variants__variant__set-winner"
                            onClick={() => {
                              setSetAsWinnerOpen({ show: true, id: variant.id });
                            }}
                            disabled={testHasWinner(abTest)}
                            text={getWinnerButtonText(abTest.testType)}
                            size={'small'}
                            variant={'ghost'}
                          />
                        )}
                    </div>
                    {setAsWinnerOpen.show && setAsWinnerOpen.id == variant.id && (
                      <ConfirmationModal
                        title={`${getWinnerButtonText(abTest.testType, true)} "${
                          abTestWidgetType === AbTestWidgetType.CHECKOUT
                            ? checkouts.find(
                                (x) =>
                                  x.id ==
                                  variant.abTestWidgets.find((x) => x.type == AbTestWidgetType.CHECKOUT)?.externalId,
                              )?.name || 'Unknown'
                            : variant.name
                        }" as winner`}
                        buttonText={getWinnerButtonText(abTest.testType)}
                        mainContent={
                          isPdpTest ? (
                            <PdpWinnerSuccessContent />
                          ) : isCrossSiteTest ? (
                            <ThemeWinnerSuccessContent />
                          ) : (
                            getSetWinnerDialogText()
                          )
                        }
                        isOpen={setAsWinnerOpen.show}
                        onClose={() => {
                          setSetAsWinnerOpen({ show: false, id: undefined });
                        }}
                        onSuccess={async () => {
                          const message = await abTestActions.setWinner(setAsWinnerOpen.id);
                          console.log('message returned from setwinner: ', message);
                          if (message.length > 0) {
                            setForceSetAsWinnerOpen({ show: true, id: setAsWinnerOpen.id, text: message });
                            setSetAsWinnerOpen({ show: false, id: undefined });
                            return;
                          }
                          setSetAsWinnerOpen({ show: false, id: undefined });
                          // Only redirect for checkout tests
                          if (abTestWidgetType === AbTestWidgetType.CHECKOUT) {
                            angularRedirect(
                              `/convert/checkout-groups?active=${variant.abTestWidgets.find((x) => x.type == AbTestWidgetType.CHECKOUT).externalId}`,
                            );
                          }
                        }}
                      />
                    )}
                    {forceSetAsWinnerOpen.show && forceSetAsWinnerOpen.id == variant.id && (
                      <ConfirmationModal
                        title={`${getWinnerButtonText(abTest.testType, true)} "${
                          abTestWidgetType === AbTestWidgetType.CHECKOUT
                            ? checkouts.find(
                                (x) =>
                                  x.id ==
                                  variant.abTestWidgets.find((x) => x.type == AbTestWidgetType.CHECKOUT)?.externalId,
                              )?.name || 'Unknown'
                            : variant.name
                        }" as winner`}
                        buttonText={getWinnerButtonText(abTest.testType)}
                        mainContent={
                          <WarningBanner title={forceSetAsWinnerOpen.text} content={''} hideCloseButton={true} />
                        }
                        isOpen={forceSetAsWinnerOpen.show}
                        disableConfirmation={forceSetAsWinnerOpen.text.includes('Operation is blocked')}
                        onClose={() => {
                          setForceSetAsWinnerOpen({ show: false, id: undefined, text: undefined });
                        }}
                        onSuccess={async () => {
                          await abTestActions.setWinnerForce(forceSetAsWinnerOpen.id);
                          setForceSetAsWinnerOpen({ show: false, id: undefined, text: '' });
                          // Only redirect for checkout tests
                          if (abTestWidgetType === AbTestWidgetType.CHECKOUT) {
                            angularRedirect(
                              `/convert/checkout-groups?active=${variant.abTestWidgets.find((x) => x.type == AbTestWidgetType.CHECKOUT).externalId}`,
                            );
                          }
                        }}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}

      <div className="ab-test-item__divider"></div>

      {startAbTestOpen && (
        <ConfirmationModal
          title={'Start A/B test?'}
          buttonText={'Start test'}
          mainContent={`You are about to start test "${abTest.name}"`}
          isOpen={startAbTestOpen}
          onClose={() => {
            setStartAbTestOpen(false);
          }}
          onSuccess={async () => {
            await abTestActions.startTest(abTest.id);
            setStartAbTestOpen(false);
          }}
        />
      )}
      {deleteAbTestOpen && (
        <ConfirmationModal
          title={'Are you sure?'}
          buttonText={'Delete'}
          mainContent={`You’re deleting the A/B test ${abTest?.name}`}
          isOpen={deleteAbTestOpen != null}
          onClose={() => {
            setDeleteAbTestOpen(false);
          }}
          onSuccess={async () => {
            await abTestActions.deleteTest(abTest.id, getAbTestType(abTest));
            setDeleteAbTestOpen(false);
          }}
        />
      )}
      {endAbTestOpen && (
        <ConfirmationModal
          title={'End A/B test?'}
          buttonText={'End test'}
          mainContent={`You’re ending A/B test "${abTest?.name}"`}
          isOpen={endAbTestOpen}
          onClose={() => {
            setEndAbTestOpen(false);
          }}
          onSuccess={async () => {
            await abTestActions.endTest(abTest.id);
            setEndAbTestOpen(false);
          }}
        />
      )}
      {editAbTestOpen &&
        (useAbTestModalV2 ? (
          <AbTestModalV2
            isOpen={editAbTestOpen}
            existingAbTest={abTest}
            onClose={() => {
              setEditAbTestOpen(false);
            }}
            onSaveSuccess={() => {
              setEditAbTestOpen(false);
            }}
            saveTest={abTestActions.upsertTest}
            mode="edit"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ) : (
          <AbTestModal
            isOpen={editAbTestOpen}
            existingAbTest={abTest}
            onClose={() => {
              setEditAbTestOpen(false);
            }}
            onSaveSuccess={() => {
              setEditAbTestOpen(false);
            }}
            saveTest={abTestActions.upsertTest}
            mode="edit"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ))}
      {viewAbTestOpen &&
        (useAbTestModalV2 ? (
          <AbTestModalV2
            isOpen={viewAbTestOpen}
            existingAbTest={abTest}
            onClose={() => {
              setViewAbTestOpen(false);
            }}
            onSaveSuccess={() => {
              setViewAbTestOpen(false);
            }}
            mode="view"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ) : (
          <AbTestModal
            isOpen={viewAbTestOpen}
            existingAbTest={abTest}
            onClose={() => {
              setViewAbTestOpen(false);
            }}
            onSaveSuccess={() => {
              setViewAbTestOpen(false);
            }}
            mode="view"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ))}
      {duplicateAbTestOpen &&
        (useAbTestModalV2 ? (
          <AbTestModalV2
            isOpen={duplicateAbTestOpen}
            existingAbTest={createAbTestDuplicate(abTest)}
            onClose={() => {
              setDuplicateAbTestOpen(false);
            }}
            onSaveSuccess={() => {
              setDuplicateAbTestOpen(false);
              setActiveTab('Drafts');
            }}
            saveTest={abTestActions.upsertTest}
            mode="duplicate"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ) : (
          <AbTestModal
            isOpen={duplicateAbTestOpen}
            existingAbTest={createAbTestDuplicate(abTest)}
            onClose={() => {
              setDuplicateAbTestOpen(false);
            }}
            onSaveSuccess={() => {
              setDuplicateAbTestOpen(false);
              setActiveTab('Drafts');
            }}
            saveTest={abTestActions.upsertTest}
            mode="duplicate"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ))}
      {archiveAbTestOpen && (
        <ConfirmationModal
          title={'Archiving AB Test'}
          buttonText={'Confirm'}
          mainContent={`Archiving an AB Test will make it inaccessible for users within PDQ. Click "confirm" to proceed.`}
          isOpen={archiveAbTestOpen}
          onClose={() => {
            setArchiveAbTestOpen(false);
          }}
          onSuccess={async () => {
            await abTestActions.archiveTest(abTest.id);
            setArchiveAbTestOpen(false);
          }}
        />
      )}
    </div>
  );
};

const getAbTestStatusText = (abTest: AbTest): string => {
  if (abTest.endedAt) return 'Concluded';
  if (abTest.startedAt) return 'Running';

  return 'Draft';
};

const getAbTestCategoryTypeLabel = (abTest: AbTest): string => {
  if (abTest.testType) {
    return AbTestWidgetTypeLabels[abTest.testType] || abTest.testType;
  }

  const widgetType = abTest.abTestVariants?.[0]?.abTestWidgets?.[0]?.type;

  if (!widgetType) {
    return 'Unknown Test';
  }

  return AbTestWidgetTypeLabels[widgetType];
};

const getColorByStatus = (color: string): 'gray' | 'blue' | 'orange' => {
  switch (color) {
    case 'Concluded':
      return 'gray';
    case 'Running':
      return 'blue';
    case 'Draft':
      return 'orange';
    default:
      return 'orange';
  }
};
const getAbTestThreeDotMenuItems = (
  abTest: AbTest,
  modalActions,
  angularRedirect: (url: string) => void,
  showToast,
) => {
  const type = getAbTestType(abTest);
  if (type === AbTestType.DRAFT) {
    return [
      {
        label: 'Start',
        onClick: async () => {
          modalActions.setStartAbTestOpen(true);
        },
      },
      {
        label: 'Edit',
        onClick: async () => {
          modalActions.setEditAbTestOpen(true);
        },
      },
      {
        label: 'Duplicate',
        onClick: async () => {
          modalActions.setDuplicateAbTestOpen(true);
        },
      },
      {
        label: 'Copy test ID',
        onClick: () => {
          const testId = getAbTestDuplicationTestIdString(abTest);
          if (!navigator?.clipboard) {
            alert(`Your browser does not support copying to clipboard. Test ID is: ${testId}`);
            return;
          }
          navigator.clipboard.writeText(testId).then(() => {
            showToast({ message: 'Test ID copied to clipboard.', mode: 'success' });
          });
        },
      },
      {
        label: 'Delete',
        onClick: async () => {
          modalActions.setDeleteAbTestOpen(true);
        },
      },
    ];
  } else if (type === AbTestType.RUNNING) {
    return [
      {
        label: 'View',
        onClick: async () => {
          modalActions.setViewAbTestOpen(true);
        },
      },
      {
        label: 'End test',
        onClick: async () => {
          modalActions.setEndAbTestOpen(true);
        },
      },
      {
        label: 'Get analytics',
        onClick: async () => {
          angularRedirect(generateAnalyticsDashboardUrl(abTest));
        },
      },
      {
        label: 'Duplicate',
        onClick: async () => {
          modalActions.setDuplicateAbTestOpen(true);
        },
      },
    ];
  } else if (type === AbTestType.CONCLUDED) {
    return [
      {
        label: 'View',
        onClick: async () => {
          modalActions.setViewAbTestOpen(true);
        },
      },
      {
        label: 'Get analytics',
        onClick: async () => {
          angularRedirect(generateAnalyticsDashboardUrl(abTest));
        },
      },
      {
        label: 'Duplicate',
        onClick: async () => {
          modalActions.setDuplicateAbTestOpen(true);
        },
      },
      {
        label: 'Archive',
        onClick: async () => {
          modalActions.setArchiveAbTestOpen(true);
        },
      },
    ];
  }
  return [];
};

const testHasWinner = (abTest: AbTest) => {
  return abTest.abTestVariants.some((x) => x.winnerAt != null);
};

const testHasRules = (abTest: AbTest) => {
  return abTest.abTestRules ? true : false;
};

const getDateRangeDisplayDate = (date: Date | null) => {
  if (!date) {
    return 'ongoing';
  }
  const dateObj = new Date(date);
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();
  const month = dateObj.toLocaleString('en-US', { month: 'short' });
  const formattedDate = day + ' ' + month + ' ' + year;

  return formattedDate;
};
function createAbTestDuplicate(abTest: AbTest): AbTest {
  return {
    ...abTest,
    id: undefined,
    startedAt: undefined,
    endedAt: undefined,
    archivedAt: undefined,
    createdAt: undefined,
    testComments: undefined,
    winnerAt: undefined,
    name: `Copy - ${abTest.name}`,
    testType: abTest.testType,
    abTestVariants: abTest.abTestVariants.map((variant) => ({
      ...variant,
      id: undefined,
      abTestWidgets: variant.abTestWidgets.map((widget) => ({ ...widget, id: undefined })),
    })),
  };
}
